import { User, useAuth0 } from "@auth0/auth0-react";
import "pepjs";
import React, { Suspense, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { APIContextProvider } from "./APIContext";
import { APIDataContextProvider } from "./APIDataContext";
import { CATADMIN_URL } from "./auth";
import BaseLayout from "./BaseLayout";
import { DtoAuthorizeResponse, InternalCatalogueAPI } from "./catadmin/InternalCatalogueAPI";
import { ErrorBoundary } from "./components/ErrorBondary";
import { GlobalErrorContext } from "./GlobalErrorContext";
import { GlobalErrorMessage } from "./GlobalErrorMessage";
import { OverlayLoading } from "./icons/Loading";
import { NotFound404 } from "./NotFound404";

const Chair = React.lazy(() => import("./myconfigura/Chair"));
const CataloguesRoutes = React.lazy(() => import("./CataloguesRoutes"));
const CmsymViewer = React.lazy(() => import("./CmsymViewer"));
const Home = React.lazy(() => import("./Home"));
const Settings = React.lazy(() => import("./Settings"));

export const FeatureContext = React.createContext<string[]>([]);
export const UserContext = React.createContext<Option<User>>(undefined);
export const AppContext = React.createContext<
	Option<{
		logout: () => Promise<void>;
	}>
>(undefined);

interface Props {}

function App(props: Props) {
	const { setError } = useContext(GlobalErrorContext);
	const {
		error: auth0Error,
		getAccessTokenSilently,
		isAuthenticated,
		isLoading: auth0Loading,
		loginWithRedirect,
		logout: auth0Logout,
		user,
	} = useAuth0();

	const [stageAuth, setStageAuth] = useState<DtoAuthorizeResponse>();
	const [sessionLoading, setSessionLoading] = useState(false);
	const [sessionError, setSessionError] = useState<Error>();
	const [loginStarted, setLoginStarted] = useState(false);
	const [loginError, setLoginError] = useState<Error>();
	const isPublicChair = window.location.pathname.split("/").filter(Boolean).pop() === "chair";

	const logout = useCallback(async () => {
		setStageAuth(undefined);
		await auth0Logout({
			logoutParams: { returnTo: window.location.origin },
		});
	}, [auth0Logout]);

	const appContextValue = useMemo(() => {
		return { logout };
	}, [logout]);

	useEffect(() => {
		if (isPublicChair || auth0Loading || isAuthenticated || auth0Error || loginStarted) {
			return;
		}

		setLoginStarted(true);
		loginWithRedirect().catch((err) => {
			const error = err instanceof Error ? err : new Error(`${err}`);
			setLoginError(error);
			setError("Could not start Auth0 login", error);
		});
	}, [
		auth0Error,
		auth0Loading,
		isAuthenticated,
		isPublicChair,
		loginStarted,
		loginWithRedirect,
		setError,
	]);

	useEffect(() => {
		if (auth0Error) {
			setError("Could not authenticate with Auth0", auth0Error);
		}
	}, [auth0Error, setError]);

	useEffect(() => {
		if (isPublicChair || auth0Loading || !isAuthenticated) {
			if (!isAuthenticated) {
				setStageAuth(undefined);
				setSessionError(undefined);
			}
			return;
		}

		let canceled = false;
		setSessionLoading(true);
		setSessionError(undefined);

		getAccessTokenSilently()
			.then((accessToken) => {
				const internalCatalogueAPI = new InternalCatalogueAPI();
				return internalCatalogueAPI.postSessionToken(`Bearer ${accessToken}`, CATADMIN_URL);
			})
			.then((auth) => {
				if (canceled) return;
				setStageAuth(auth);
			})
			.catch((err) => {
				if (canceled) return;
				const error = err instanceof Error ? err : new Error(`${err}`);
				setStageAuth(undefined);
				setSessionError(error);
				setError("Could not create Stage API session", error);
			})
			.finally(() => {
				if (!canceled) {
					setSessionLoading(false);
				}
			});

		return () => {
			canceled = true;
		};
	}, [auth0Loading, getAccessTokenSilently, isAuthenticated, isPublicChair, setError]);

	if (isPublicChair) {
		return (
			<React.StrictMode>
				<Router>
					<Routes>
						<Route
							path="chair"
							element={
								<Suspense fallback={<OverlayLoading />}>
									<Chair />
								</Suspense>
							}
						/>
					</Routes>
				</Router>
			</React.StrictMode>
		);
	}

	if (auth0Error || loginError || sessionError) {
		return <GlobalErrorMessage />;
	}

	if (auth0Loading || !isAuthenticated || sessionLoading || stageAuth === undefined) {
		return <OverlayLoading />;
	}

	const features = stageAuth.apiSession.features || [];
	const permissions = stageAuth.apiSession.permissions || [];

	return (
		<React.StrictMode>
			<AppContext.Provider value={appContextValue}>
				<APIContextProvider auth={stageAuth}>
					<APIDataContextProvider>
						<UserContext.Provider value={user}>
							<FeatureContext.Provider value={features}>
								<ErrorBoundary>
									<Suspense fallback={<OverlayLoading />}>
										<Router>
											<Routes>
												<Route
													path="chair"
													element={
														<Suspense fallback={<></>}>
															<Chair />
														</Suspense>
													}
												></Route>
												<Route path="" element={<Home />} />
												<Route
													path="settings"
													element={<Settings setAuth={setStageAuth} />}
												/>
												<Route
													path="catalogue/*"
													element={
														<CataloguesRoutes
															permissions={permissions}
														/>
													}
												/>
												<Route
													path="cmsym-viewer"
													element={<CmsymViewer />}
												/>

												<Route
													path="*"
													element={
														<BaseLayout>
															<NotFound404></NotFound404>
														</BaseLayout>
													}
												/>
											</Routes>
										</Router>
									</Suspense>
								</ErrorBoundary>
							</FeatureContext.Provider>
						</UserContext.Provider>
					</APIDataContextProvider>
				</APIContextProvider>
			</AppContext.Provider>
		</React.StrictMode>
	);
}

export default App;
